import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface TatBucketSummary {
  readonly bucket: string;
  readonly activeCount: number;
  readonly breachedCount: number;
  readonly totalCount: number;
  readonly thresholdMinutes: number;
}
interface TatSummary {
  readonly generatedAt: string;
  readonly buckets: readonly TatBucketSummary[];
  readonly totalActive: number;
  readonly totalBreached: number;
}

const BUCKET_LABELS: Record<string, string> = {
  REVIEW: 'Review',
  ASSIGNMENT: 'Assignment',
  VENDOR_ACCEPTANCE: 'Vendor acceptance',
  EXECUTION_START: 'Execution start',
};

export function BoTatDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<TatSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<TatSummary>('/v1/branding-officer/tat', { token })
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <main aria-label="bo-tat"><p role="alert">{error}</p></main>;
  if (!data) return <main aria-label="bo-tat"><p>{t('bo.tat.loading')}</p></main>;

  return (
    <main aria-label="bo-tat">
      <h1>{t('bo.tat.title')}</h1>
      <ul aria-label="tat-totals">
        <li aria-label="tat-total-active">{t('bo.tat.totalActive')}: {data.totalActive}</li>
        <li aria-label="tat-total-breached">{t('bo.tat.totalBreached')}: {data.totalBreached}</li>
      </ul>

      {data.buckets.length === 0 ? (
        <p aria-label="tat-empty">{t('bo.tat.empty')}</p>
      ) : (
        <table aria-label="tat-table">
          <thead>
            <tr><th>{t('bo.tat.col.phase')}</th><th>{t('bo.tat.col.active')}</th><th>{t('bo.tat.col.breached')}</th><th>{t('bo.tat.col.total')}</th><th>{t('bo.tat.col.threshold')}</th></tr>
          </thead>
          <tbody>
            {data.buckets.map((b) => (
              <tr key={b.bucket} aria-label={`tat-row-${b.bucket}`}>
                <td>{BUCKET_LABELS[b.bucket] ? t(`bo.tat.bucket.${b.bucket}`) : b.bucket}</td>
                <td aria-label={`tat-active-${b.bucket}`}>{b.activeCount}</td>
                <td aria-label={`tat-breached-${b.bucket}`}>{b.breachedCount}</td>
                <td>{b.totalCount}</td>
                <td>{b.thresholdMinutes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
